import { t } from "i18next";
import { Button, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { useContext, useEffect, useState } from "react";
import { emptyUser, User } from "../../../../interfaces/user";
import { getUserSingle, updateUser } from "../../../../services/user";
import Select from 'react-select';
import { getClubOptions } from "../../../../services/club";
import { Club } from "../../../../interfaces/club";
import { roleOptions } from "../../../../data/roles";
import { UserContext } from "../../../../App";
import { LoadingSpinner } from "../../../general/loading-spinner";

interface AdminUserEditProps {
    id: number;
    onSave: CallableFunction;
    onClose: CallableFunction;
}

export function AdminUserEdit(props: AdminUserEditProps) {
    const { userProfile } = useContext(UserContext);

    const [user, setUser] = useState<User>(emptyUser);
    const [clubOptions, setClubOptions] = useState<any[]>([]);
    const [clubs, setClubs] = useState<any[]>([]);
    const [defaultClub, setDefaultClub] = useState<any[]>([]);
    const [defaultRole, setDefaultRole] = useState('');
    const [selectedRole, setSelectedRole] = useState<any>({});
    const [status, setStatus] = useState('0');

    useEffect(() => {
        getClubOptions()
            .then(response => {
                setClubOptions(response.data.map((c: Club) => {
                    return {
                        value: c.id,
                        label: c.name
                    }
                }));
            })
            .catch(error => {
                console.log(error);
            });

        getUserSingle(props.id)
            .then(response => {
                setUser(response.data);
                setStatus(response.data.status);
                setDefaultClub(response.data.club.map((c: any) => {
                    return {
                        value: c.id,
                        label: c.name
                    }
                }));
                setClubs(response.data.club.map((c: any) => {
                    return {
                        value: c.id,
                        label: c.name
                    }
                }));
                if (response.data.role.length > 0) {
                    setDefaultRole(response.data.role[0].id);
                    const index = roleOptions.findIndex((r: any) => r.value == response.data.role[0].id);
                    if (index !== -1) {
                        setSelectedRole(roleOptions[index]);
                    }
                }
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const handleSave = (event: any) => {
        event.preventDefault();

        const body = {
            id: event.target.id.value,
            first_name: event.target.first_name.value,
            last_name: event.target.last_name.value,
            email: event.target.email.value,
            phone: event.target.phone.value,
            document_id: event.target.document_id.value,
            birthdate: event.target.birthdate.value,
            club: clubs,
            role: event.target.role.value,
            status: status ? 1 : 0
        }
        updateUser(body)
            .then(response => {
                if (response.data.status == 1) {
                    props.onSave(response.data.data);
                } else {
                    alert(response.data.message);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }
    
    const handleClose = () => {
        props.onClose();
    }

    const handleClubChange = (clubs: any) => {
        setClubs(clubs);
    }
    
    const handleRoleChange = (event: any) => {
        event.preventDefault();
        setClubs([]);
        const index = roleOptions.findIndex((r: any) => r.value == event.currentTarget.value);
        if (index !== -1) {
            setSelectedRole(roleOptions[index]);
        } else {
            setSelectedRole({});
        }
    }

    const handleStatusChange = (event: any) => {
        setStatus(event.currentTarget.checked);
    }

    return (
        <Modal className="admin-user-new-container" show={ true }
            onHide={ handleClose } size="lg"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("edit_user") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { user.id == 0 && 
                    <LoadingSpinner />
                }
                { user.id != 0 && 
                    <Form onSubmit={ handleSave }>
                        <div className="fields">
                            <Container>
                                <Row>
                                    <Form.Control type="hidden" name="id" required defaultValue={ user.id }/>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="first_name">
                                            <Form.Label aria-required="true">{ t("first_name") }</Form.Label>
                                            <Form.Control type="text" name="first_name" required defaultValue={ user.first_name }/>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="last_name">
                                            <Form.Label aria-required="true">{ t("last_name") }</Form.Label>
                                            <Form.Control type="text" name="last_name" required defaultValue={ user.last_name } />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="email">
                                            <Form.Label>{ t("email") }</Form.Label>
                                            <Form.Control type="text" name="email" defaultValue={ user.email } disabled />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="phone">
                                            <Form.Label>{ t("phone") }</Form.Label>
                                            <Form.Control type="text" name="phone" defaultValue={ user.phone } />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="document_id">
                                            <Form.Label>{ t("document_id") }</Form.Label>
                                            <Form.Control type="text" name="document_id" defaultValue={ user.document_id } />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="birthdate">
                                            <Form.Label aria-required="true">{ t("birthdate") }</Form.Label>
                                            <Form.Control type="date" name="birthdate" required defaultValue={ user.birthdate } />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group className="mb-3" controlId="role">
                                            <Form.Label aria-required="true">{ t("role") }</Form.Label>
                                            <Form.Select aria-label={ t("role") } defaultValue={ defaultRole } required onChange={ handleRoleChange }>
                                                <option value="">{ t("select_role") }</option>
                                                { roleOptions.filter((r: any) => ['badminton_administrator', 'badminton_manager'].includes(r.value)).map((r: any) => {
                                                    return <option key={ 'role-' + r.value } value={r.value}>{ t(r.label) }</option>
                                                })}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    { selectedRole.value != 'badminton_administrator' &&
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mb-3" controlId="club">
                                                <Form.Label aria-required="true">{ t("club") }</Form.Label>
                                                <Select name="club" required isMulti={ true } options={clubOptions}
                                                    defaultValue={ defaultClub } onChange={ handleClubChange } />
                                            </Form.Group>
                                        </Col>
                                    }
                                    { user.id != userProfile.id && 
                                        <Col xs={12}>
                                            <Form.Check
                                                className="mt-3"
                                                type="switch"
                                                name="status"
                                                label={ t("user_status_switch_label") }
                                                defaultChecked={ user.status == 1 ? true : false }
                                                id="status-switch"
                                                onChange= { handleStatusChange }
                                            />
                                        </Col>
                                    }
                                    { user.id == userProfile.id &&
                                        <Form.Control type="hidden" name="status" required defaultValue={ user.status }/>
                                    }
                                </Row>
                            </Container>
                        </div>
                        <div className="actions">
                            <Button type="submit" variant="primary">
                                { t("Save") }
                            </Button>
                            <Button variant="secondary" onClick={ handleClose }>
                                { t("Close") }
                            </Button>
                        </div>
                    </Form>
                }
            </Modal.Body>
        </Modal>
    );
}